import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import MatchesPage from './pages/MatchesPage';
import CreateMatchPage from './pages/CreateMatchPage';
import MatchDetailPage from './pages/MatchDetailPage';
import PlayersPage from './pages/PlayersPage';
import PlayerDetailPage from './pages/PlayerDetailPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import RankingPage from './pages/RankingPage';
import QRCodePage from './pages/QRCodePage';
import AdminPage from './pages/AdminPage';
import AdminMatchesPage from './pages/AdminMatchesPage';
import AdminRankingPage from './pages/AdminRankingPage';
import BachecaPage from './pages/BachecaPage';
import BookLessonPage from './pages/BookLessonPage';
import MyLessonsPage from './pages/MyLessonsPage';
import AdminLessonsPage from './pages/AdminLessonsPage';
import GestionaleLessonsPage from './pages/GestionaleLessonsPage';
import RegolamentoPage from './pages/RegolamentoPage';
import TournamentsPage from './pages/TournamentsPage';
import CreateTournamentPage from './pages/CreateTournamentPage';
import TournamentDetailPage from './pages/TournamentDetailPage';
import AnalyticsPage from './pages/AnalyticsPage';
import './App.css';

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Determine if we should show the header
  const showHeader = !['/login', '/'].includes(location.pathname) && 
                     !location.pathname.startsWith('/auth');

  return (
    <div className="min-h-screen bg-slate-50">
      {showHeader && <Header />}
      <main className={showHeader ? 'pb-20 sm:pb-0' : ''}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/matches" element={
            <ProtectedRoute>
              <MatchesPage />
            </ProtectedRoute>
          } />
          <Route path="/matches/new" element={
            <ProtectedRoute>
              <CreateMatchPage />
            </ProtectedRoute>
          } />
          <Route path="/matches/:matchId" element={
            <ProtectedRoute>
              <MatchDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/players" element={
            <ProtectedRoute>
              <PlayersPage />
            </ProtectedRoute>
          } />
          <Route path="/players/:userId" element={
            <ProtectedRoute>
              <PlayerDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/ranking" element={
            <ProtectedRoute>
              <RankingPage />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="/chat/:conversationId" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/qr-code" element={
            <ProtectedRoute>
              <QRCodePage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/matches" element={
            <ProtectedRoute>
              <AdminMatchesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/ranking" element={
            <ProtectedRoute>
              <AdminRankingPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/bacheca" element={
            <ProtectedRoute>
              <BachecaPage />
            </ProtectedRoute>
          } />
          <Route path="/maestri/:maestroId/prenota" element={
            <ProtectedRoute>
              <BookLessonPage />
            </ProtectedRoute>
          } />
          <Route path="/lessons/my" element={
            <ProtectedRoute>
              <MyLessonsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/lessons" element={
            <ProtectedRoute>
              <AdminLessonsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/gestionale" element={
            <ProtectedRoute>
              <GestionaleLessonsPage />
            </ProtectedRoute>
          } />
          <Route path="/regolamento" element={
            <ProtectedRoute>
              <RegolamentoPage />
            </ProtectedRoute>
          } />
          <Route path="/tournaments" element={
            <ProtectedRoute>
              <TournamentsPage />
            </ProtectedRoute>
          } />
          <Route path="/tournaments/new" element={
            <ProtectedRoute>
              <CreateTournamentPage />
            </ProtectedRoute>
          } />
          <Route path="/tournaments/:tournamentId" element={
            <ProtectedRoute>
              <TournamentDetailPage />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
